import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormFieldType, SurveyForm } from '../../../../../../../../extViewmodels';
import { Checkbox } from '../../../../../../../../web/components/Checkbox';
import { Input } from '../../../../../../../../web/components-v2/Input';
import { ISelectBoxOption, SelectBox } from '../../../../../../../../web/components/SelectBox';
import { baseStyleSheet } from '../../../../../../../../web/styles/styles';
import { IFormField, IPublicFileAttachment } from '../../../../../../../models/Api';
import { FileUpload } from '../../../../../../SurveyResponses/shared/FileUpload';
import { useConditionalVisibility } from '../../../../../shared/hooks/useConditionalVisibility';
import { styleSheet } from '../styles';
import { ErrorMessage } from './ErrorMessage';

interface CustomFormFieldsProps {
	fields: IFormField<any>[];
	token: string;
	surveyResponseId?: string;
	surveyForm?: SurveyForm;
	onDeleteFile?: (file: IPublicFileAttachment) => void;
}

export const CustomFormFields: React.FC<CustomFormFieldsProps> = ({
	fields,
	token,
	surveyResponseId,
	surveyForm,
	onDeleteFile,
}) => {
	const {
		control,
		formState: { errors },
		setValue,
		getValues,
	} = useFormContext();

	// Watch customProperties so conditional-visibility evaluation re-runs when answers change.
	const watchedCustomProperties = useWatch({ control, name: 'customProperties' });
	const visibleFieldIds = useConditionalVisibility(surveyForm, watchedCustomProperties ?? {});

	// Set of fieldIds represented in surveyForm.surveyParts. Used to distinguish
	// "field is governed by surveyForm and currently not visible" (hide + clear)
	// from "field is not represented in surveyForm at all" (show as default).
	// Without this, fields added to customForm.fields after surveyForm was last
	// saved — or surveys saved before surveyForm was wired up — would silently
	// disappear from the attendee view.
	const surveyPartFieldIds = React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) return undefined;
		return new Set(
			surveyForm.surveyParts.map(part => (part as { fieldId?: string }).fieldId).filter((id): id is string => !!id)
		);
	}, [surveyForm]);

	// Clear values of fields that are hidden so submitted responses don't include stale answers.
	// On first run we seed `prev` with the full set of surveyForm-governed field IDs, so a field
	// that loads already-hidden (e.g., an in-progress response with a stale answer for a question
	// whose visibility rule was added later) also gets cleared — not just visible→hidden transitions.
	// FileUpload fields: also notify the parent of any previously-uploaded attachments so they get
	// flagged for server-side deletion on submit. Otherwise the form state shows no files but the
	// uploads remain orphaned on the server.
	const prevVisibleRef = React.useRef<Set<string> | undefined>(undefined);
	React.useEffect(() => {
		const prev = prevVisibleRef.current ?? surveyPartFieldIds;
		if (!visibleFieldIds) {
			return;
		}

		if (!prev) {
			prevVisibleRef.current = visibleFieldIds;
			return;
		}

		const clearField = (fieldId: string) => {
			const field = fields.find(f => f.id === fieldId);
			if (!field) {
				return;
			}
			const fieldPath = `customProperties.${fieldId}`;

			if (field.fieldType === FormFieldType.FileUpload) {
				const existing = (getValues(fieldPath) as IPublicFileAttachment[] | undefined) ?? [];
				existing.forEach(attachment => attachment?.attachmentId && onDeleteFile?.(attachment));
				setValue(fieldPath, []);
			} else {
				setValue(fieldPath, '');
			}
		};

		for (const fieldId of prev) {
			if (!visibleFieldIds.has(fieldId)) {
				clearField(fieldId);
			}
		}

		prevVisibleRef.current = visibleFieldIds;
	}, [visibleFieldIds, surveyPartFieldIds, fields, setValue, getValues, onDeleteFile]);

	const isFieldVisible = (fieldId: string) => {
		// No surveyForm at all → no filtering.
		if (!visibleFieldIds || !surveyPartFieldIds) return true;
		// Field isn't governed by surveyForm → default to visible.
		if (!surveyPartFieldIds.has(fieldId)) return true;
		return visibleFieldIds.has(fieldId);
	};

	return (
		<>
			{fields
				?.filter(f => !f.isHidden)
				.filter(f => isFieldVisible(f.id))
				.map(f => {
					const fieldDropdownOptions = f.options?.map(o => {
						return {
							id: o.id,
							title: o.label,
							value: o.label,
						};
					});

					return (
						<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
							<label htmlFor={f.id} className={css(styleSheet.label)}>
								{f.label}
								{!f.isOptional ? (
									<>
										{' '}
										<span className={css(baseStyleSheet.required)}>*</span>
									</>
								) : null}
							</label>
							<Controller
								name={`customProperties.${f.id}`}
								control={control}
								render={({ field: { ref, ...fieldProps } }) => {
									if (f.fieldType === FormFieldType.String) {
										return (
											<>
												<Input
													type='text'
													id={f.id}
													styles={[styleSheet.textInput]}
													ref={ref}
													{...fieldProps}
													value={fieldProps.value || ''}
												/>
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									if (f.fieldType === FormFieldType.Option) {
										const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
											setValue(`customProperties.${f.id}`, selectedOption.id);
										};
										const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
											return <span>{selectedOption.title as React.ReactNode}</span>;
										};
										return (
											<>
												<SelectBox
													options={fieldDropdownOptions}
													onRenderSelectedOptionTitle={onRenderTriggerTitle}
													onSelectionChanged={onSelectionChanged}
													placeholder={<span>Select option</span>}
													triggerClassName={css(styleSheet.selectBoxtriggerButton)}
													{...fieldProps}
													selectedOption={fieldDropdownOptions?.find(x => x.id === fieldProps.value) || undefined}
												/>
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									if (f.fieldType === FormFieldType.MultipleOptions) {
										const fieldCheckboxOptions = f.options?.map(o => {
											const fieldCheckboxSelectedOptions: string[] = fieldProps?.value?.split(',') ?? [];
											/**
											 * Method to update the value of the field
											 * @param e {React.ChangeEvent<HTMLInputElement>}
											 * sets a {String} value of the field
											 */
											const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
												const newOptions = [...fieldCheckboxSelectedOptions];
												if (e.target.checked) {
													newOptions.push(o.id);
												} else {
													newOptions.splice(fieldCheckboxSelectedOptions.indexOf(o.id), 1);
												}

												setValue(`customProperties.${f.id}`, `${newOptions.join(',')}`);
											};
											/**
											 * Use the onChange method to update the value of the field
											 */
											fieldProps.onChange = onChange;
											return (
												<Checkbox
													key={o.id}
													id={o.id}
													className={css(styleSheet.checkbox)}
													checked={fieldCheckboxSelectedOptions?.includes(o.id)}
													{...fieldProps}
												>
													<span>{o.label}</span>
												</Checkbox>
											);
										});
										return (
											<>
												{fieldCheckboxOptions}
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									if (f.fieldType === FormFieldType.FileUpload) {
										const error = (errors.customProperties as any)?.[f.id];
										return (
											<FileUpload
												token={token}
												surveyType='eventRegistration'
												onFileSelected={files => {
													setValue(`customProperties.${f.id}`, files);
												}}
												files={(fieldProps.value as IPublicFileAttachment[]) ?? []}
												onDeleteFile={
													surveyResponseId
														? (file: IPublicFileAttachment) => {
																onDeleteFile?.(file);
															}
														: undefined
												}
												error={error}
											/>
										);
									}

									return null;
								}}
							/>
						</div>
					);
				})}
		</>
	);
};
